import React, {
  useEffect,
  useCallback,
  useMemo,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import { useDispatch, useSelector } from 'react-redux';

import { useElements, useStripe } from '@stripe/react-stripe-js';

import Tealium from '../../../components/dataLayer/Tealium';
import {
  setShippingAddressSteppedCheckout,
  setBillingAddress,
} from '../../../slices/cart';
import Step from '../Step';
import KountSdk from '../../../components/KountSdk';
import Spinner, { THEME_TRANSPARENT } from '../../../components/common/Spinner';
import ErrorBanner from '../../../components/common/ErrorBanner';
import AccountLoginModalLauncher from '../../../components/AccountLoginModalLauncher';
import OrderSummarySteppedWrapper from '../OrderSummarySteppedWrapper';
import OrderTotalSummary from '../OrderTotalSummary';
import OrderSummary from '../OrderSummary';
import { OrderSummaryDataSources } from '../../../hooks/useOrderDataSource';

import {
  SHIPPING_INFO_STEP,
  DELIVERY_METHODS_STEP,
  PAYMENT_STEP,
  PAYMENT_BLOCK,
  REVIEW_STEP,
  CARD_TOKEN_ERROR,
  COUPON_CODE_VALIDITY_ERROR_ORDERS,
  COUPON_CODE_VALIDITY_ERROR_CART,
} from './constants';
import messages from './messages';
import styles from './SteppedCheckout.module.scss';

import CompleteOrderButton, {
  SECTIONS,
  PAYPALIDS,
} from '../../../components/CompleteOrderButton';

import {
  paymentMethods,
  savePaymentMethodData,
  completeOrderSteppedCheckout,
  changePaymentMethod,
  createCloverCharge,
  createStripeIntent,
} from '../../../slices/payments';
import { setTealiumStatus } from '../../../slices/pdp';

import {
  setShippingMethodsForAllProductsInCart,
  setNeedToRecheckSelectedShippingMethods,
  clearAvailableShippingMethods,
} from '../../../slices/shippingMethods';
import { getGroupedSelectedShippingMethodsWithProducts } from '../../../slices/shippingMethods/shippingMethods.utils';

import stepConfig from './stepConfig';
import useGoogleAutocompleteScript from '../../../hooks/useGoogleAutocompleteScript';
import useAmazonPay from '../../../hooks/useAmazonPay';

import {
  calculateTotalGiftCards,
  isSameFullAddress,
  isSameAddress,
  isSameAddressWithName,
  scrollToElement,
  isShippingAddressValid,
  getCouponDiscountAppliedToCart,
} from '../../../utils';
import useSteppedCheckoutError from '../../../hooks/useSteppedCheckoutError';
import usePrevious from '../../../hooks/usePrevious';
import AmazonPayButton from '../../../components/AmazonPayButton';
import PaypalButton from '../../../components/PaypalButton';
import { getActiveCustomer, setCustomerId } from '../../../slices/accounts';
import useQueryParams from '../../../hooks/useQueryParams';
import { selectShippingAddress } from '../../../store/selectors';
import storage from '../../../utils/storage';
import { selectIsOverwritePaidInHomeShippingABTest } from '../../../store/selectors/cart';
import { useCloverContext } from '../../../contexts/CloverContext';
import { getCookie } from '../../../utils/common';
import { COMPLETE_ORDER_BTN_ID } from '../../../components/CompleteOrderButton/CompleteOrderButton';
import useGetActiveCart from '../../../hooks/queries/useGetActiveCart/useGetActiveCart';
import turnstileService from '../../../services/turnstile';
import CheckoutProgressBar from '../../../components/CheckoutProgressBar';
import CheckoutGuaranties from '../CheckoutGuaranties';
import useShopPaySession from '../../../hooks/useShopPaySession';
import useStripeExpress from '../../../hooks/useStripeExpress';
import services from '../../../services/cart';
import TermsOfUse from '../TermsOfUse/TermsOfUse';
import {
  STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
  STRIPE_EXPRESS_CHECKOUT_FEATURE_TOGGLE,
  STRIPE_REDIRECTION_IF_REQUIRED_FEATURE_TOGGLE,
} from '../../../utils/common/constants';

import store from '../../../store';

import ShopPayButton from '../../../components/ShopPayButton';
import StripeElementsContext from '../../../contexts/StripeElementsContext';
import ExpressPayments from '../../../components/ExpressPayments';
import TurnstileWidget from '../../../components/TurnstileWidget';
import usePaymentMethodsSelector from '../../../hooks/usePaymentMethodsSelector';
import { useMutationObserver } from '../../../hooks/useMutationObserver';
import { waitForUtag } from '../../../utils/tealium';
import SteppedCheckoutSkeleton from '../SteppedCheckoutSkeleton';

const SteppedCheckout = ({ showExpressCheckoutOptions, isDraftCheckout }) => {
  useShopPaySession();

  const stripe = useStripe();
  const elements = useElements();

  const { changeSelectedPaymentMethod } = usePaymentMethodsSelector();
  const dispatch = useDispatch();
  const { cloverObject, setCloverFormErrors, cloverFormErrors } =
    useCloverContext();
  const { formatMessage, locale } = useIntl();
  const [
    amazonPayApproved,
    amazonCheckoutSessionIdFromUrl,
    amazonPayCheckoutCreated,
    stripePaymentIntentIdFromUrl,
    stripePaymentIntentClientSecretFromUrl,
  ] = useQueryParams([
    'amazonPayApproved',
    'amazonCheckoutSessionId',
    'amazonPayCheckoutCreated',
    'payment_intent',
    'payment_intent_client_secret',
  ]);

  const stripeIsExpressButton = localStorage.getItem('stripe_confirmation');

  const {
    order: {
      currentStep,
      editingStep,
      id: orderId,
      status: orderStatus,
      errorCode: orderErrorCode,
      errorMessage: orderErrorMessage,
    },
    accounts: { customerId },
    payments: {
      paymentMethodData,
      paypalOrderId,
      selectedPaymentMethod,
      applePaymentData,
      status: paymentStatus,
      paymentToken,
    },
    billingAddress: { billingAddressDraft },
    cart,
    shippingMethods: {
      needToRecheckSelectedShippingMethods,
      status: shippingMethodStatus,
      setShippingMethodStatus,
    },
  } = useSelector((state) => state);

  const { isOtherError, errorMessage } = useSteppedCheckoutError();

  const showSkeleton =
    stripeIsExpressButton && currentStep !== REVIEW_STEP && !errorMessage;

  useGetActiveCart({ cartId: cart?.id, isDraft: isDraftCheckout });

  const shippingAddress = useSelector(selectShippingAddress);

  const {
    giftCards: giftCardsApplied,
    taxedPrice,
    totalPrice,
    billingAddress,
    isDraft,
    store: { typeId: storeTypeId } = {},
  } = cart;
  const [
    isCouponCodeRedeemedValidityError,
    setisCouponCodeRedeemedValidityError,
  ] = useState(false);
  const [isCouponCodeValidityError, setisCouponCodeValidityError] =
    useState(false);
  const [checkoutErrorMessage, setCheckoutErrorMessage] = useState('');
  const hasLineItems = useRef(false);

  const setLoggedInCustomerId = useCallback(
    (loggedInCustomerId) => {
      dispatch(setCustomerId(loggedInCustomerId));
    },
    [dispatch],
  );

  const lineItemsString = JSON.stringify(cart?.lineItems);

  const isMobile = window.innerWidth <= 768;

  const showShopPayEmailCapture = window?.purTogglesEnabled(
    'shop_pay_email_field',
  );

  const isStripeRegularCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
  );

  const isStripeExpressCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_EXPRESS_CHECKOUT_FEATURE_TOGGLE,
  );

  const isStripeRedirectionIfRequiredEnabled = window?.purTogglesEnabled?.(
    STRIPE_REDIRECTION_IF_REQUIRED_FEATURE_TOGGLE,
  );

  const isStripeEnabled =
    isStripeRegularCheckoutEnabled || isStripeExpressCheckoutEnabled;

  const isCloverEnabled = !isStripeRegularCheckoutEnabled;

  const truemedButtonRef = useRef(null);
  const acimaButtonRef = useRef(null);
  const shopPayButtonRef = useRef(null);
  const chargeAfterButtonRef = useRef(null);
  const affirmButtonRef = useRef(null);

  const mutationObserverCallback = (mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        const firstNodeAdded = mutation?.addedNodes?.[0];

        const firstNodeAddedTagName = firstNodeAdded?.tagName ?? '';

        const SHOP_PAY_REQUEST_BUTTON_TAG_NAME =
          'SHOP-PAY-PAYMENT-REQUEST-BUTTON';
        if (firstNodeAddedTagName === SHOP_PAY_REQUEST_BUTTON_TAG_NAME) {
          shopPayButtonRef.current = firstNodeAdded;
        }
      }
    });
  };

  const observerOptions = {
    childList: true,
    subtree: true,
  };

  useMutationObserver(
    shopPayButtonRef,
    mutationObserverCallback,
    observerOptions,
  );

  const checkCartCouponCodeValidity = useCallback(async () => {
    const lineItemsParsed = JSON.parse(lineItemsString);
    // handle out of stock items
    const couponDiscountApplied = getCouponDiscountAppliedToCart({
      lineItems: lineItemsParsed,
    });

    if (couponDiscountApplied?.length) {
      try {
        await services.checkCartCouponCodeValidity(
          cart?.id,
          couponDiscountApplied,
        );
      } catch (error) {
        const apiErrors = error.apiErrors?.[0];
        if (apiErrors?.code === COUPON_CODE_VALIDITY_ERROR_CART) {
          if (
            apiErrors?.meta?.[0]?.error?.error_code === 'Coupon_Already_Used'
          ) {
            setisCouponCodeRedeemedValidityError(true);
          } else {
            setisCouponCodeValidityError(true);
          }
        }
      }
    }
  }, [cart?.id, lineItemsString]);

  useEffect(() => {
    if (!hasLineItems.current && JSON.parse(lineItemsString)?.length) {
      checkCartCouponCodeValidity();
      hasLineItems.current = true;
    }
    // run only on page load
  }, [checkCartCouponCodeValidity, lineItemsString]);

  // Tealium utag console error capture
  useEffect(() => {
    const handleConsoleError = (consoleErrorMessage) => {
      window?.utag?.link({
        event_name: 'checkout error',
        error: JSON.stringify(consoleErrorMessage),
      });
    };

    const isReactError = (args) => {
      // Implement logic to determine if the args represent a React console error
      // This often requires checking the message content
      const message = args[0] || '';
      let isWarning;
      const typm = typeof message;

      if (typm === 'string') {
        isWarning = message?.includes('Warning:');
      } else if (typm === 'object') {
        isWarning = message.some(
          (msg) => typeof msg === 'string' && msg.includes('Warning'),
        );
      }
      return !isWarning; // Return true if the args represent a React console error, false otherwise
    };
    // eslint-disable-next-line no-console
    const originalConsoleError = console.error;

    // eslint-disable-next-line no-console
    console.error = (...args) => {
      const isError = isReactError(...args);
      if (isError) {
        handleConsoleError(...args);
      }
      originalConsoleError.apply(console, args);
    };
  }, []);

  useEffect(() => {
    if (editingStep) {
      dispatch(setTealiumStatus({ tealiumStatus: false }));
    }
  }, [dispatch, editingStep]);

  useEffect(() => {
    if (customerId) {
      dispatch(getActiveCustomer(customerId));
    }
  }, [customerId, dispatch]);

  useAmazonPay({
    amazonPayCheckoutCreated,
    amazonPayApprovedFromUrl: amazonPayApproved,
    amazonCheckoutSessionIdFromUrl,
  });

  useStripeExpress({
    stripePaymentIntentIdFromUrl,
    stripePaymentIntentClientSecretFromUrl,
    stripeIsExpressButton,
  });

  const previousCartShippingAddress = usePrevious(shippingAddress);

  const previousCartShippingAddressIsEmpty =
    previousCartShippingAddress &&
    Object.keys(previousCartShippingAddress).length === 0;

  const previousCustomerId = usePrevious(customerId);

  const previousCurrentStep = usePrevious(currentStep);

  const doesNeedToMarkRecheckShippingMethodsInRedux =
    !previousCartShippingAddressIsEmpty &&
    shippingAddress &&
    previousCartShippingAddress &&
    !isSameAddress(previousCartShippingAddress, shippingAddress);

  useEffect(() => {
    if (
      doesNeedToMarkRecheckShippingMethodsInRedux &&
      (!customerId || previousCustomerId !== customerId)
    ) {
      dispatch(clearAvailableShippingMethods());
      dispatch(setNeedToRecheckSelectedShippingMethods(true));
    }
  }, [
    customerId,
    dispatch,
    doesNeedToMarkRecheckShippingMethodsInRedux,
    previousCustomerId,
  ]);

  const isOrderLoading = orderStatus === 'loading';
  const isOrderSuccess = orderStatus === 'succeeded';
  const isCartLoading = cart.status === 'loading';
  const isPaymentLoading = paymentStatus === 'loading';
  const isPaymentVerifying = paymentStatus === 'verifying';
  const hasCloverFormErrors =
    cloverFormErrors && Object.keys(cloverFormErrors).length > 0;
  const isShippingMethodLoading = shippingMethodStatus === 'loading';
  const isSetShippingMethodLoading = setShippingMethodStatus === 'loading';
  const showAccountLoginModalLauncher =
    currentStep === SHIPPING_INFO_STEP && !customerId;

  const isOverwritePaidInHomeShippingABTest = useSelector(
    selectIsOverwritePaidInHomeShippingABTest,
  );

  useEffect(() => {
    if (cart.status === 'succeeded' && !cart.lineItems?.length) {
      // cart is empty, then redirect to cart page
      window.location.href = '/cart';
    }
  }, [cart.lineItems?.length, cart.status]);

  const isLoading =
    isOrderLoading ||
    isCartLoading ||
    isPaymentLoading ||
    isShippingMethodLoading ||
    isSetShippingMethodLoading;

  useEffect(() => {
    if (
      orderErrorCode &&
      orderErrorCode === COUPON_CODE_VALIDITY_ERROR_ORDERS
    ) {
      if (orderErrorMessage?.[0]?.error?.error_code === 'Coupon_Already_Used') {
        setisCouponCodeRedeemedValidityError(true);
      } else if (
        orderErrorMessage?.[0]?.error?.error_code === 'Coupon_Does_Not_Exist'
      ) {
        setisCouponCodeValidityError(true);
      }
    }
  }, [orderErrorCode, orderErrorMessage]);

  useEffect(() => {
    if (
      amazonCheckoutSessionIdFromUrl &&
      (amazonPayCheckoutCreated || amazonPayApproved)
    ) {
      dispatch(changePaymentMethod(paymentMethods.AMAZON_PAY));
    }
  }, [
    amazonCheckoutSessionIdFromUrl,
    amazonPayApproved,
    amazonPayCheckoutCreated,
    dispatch,
  ]);

  useEffect(() => {
    if (orderId) {
      localStorage.setItem('orderId', orderId);

      // Shop Pay Session will redirect to order confirmation page after the payment is confirmed
      // otherwise if we move to the order confirmation page, there's a chance that the order is not confirmed in ShopPay
      if (selectedPaymentMethod !== paymentMethods.SHOP_PAY) {
        window.location.href = '/order-confirmation';
      }
    }
  }, [orderId, selectedPaymentMethod]);

  const totalFromStore = taxedPrice ? taxedPrice.totalGross : totalPrice;

  const isTotalFromStoreZero = totalFromStore.centAmount === 0;

  const isTotalCoveredByGiftcards =
    totalFromStore.centAmount -
      calculateTotalGiftCards({ giftCards: giftCardsApplied }) <=
    0;

  useGoogleAutocompleteScript();

  useEffect(() => {
    if (customerId) {
      dispatch(getActiveCustomer(customerId));
    }
  }, [customerId, dispatch]);

  const groupedSelectedShippingMethodsWithProducts =
    getGroupedSelectedShippingMethodsWithProducts({ cart, locale });

  const hasSelectedShippingMethodsForAllTheLineItems =
    groupedSelectedShippingMethodsWithProducts?.shippingMethods?.length > 0 &&
    cart.lineItems?.every((lineItem) =>
      groupedSelectedShippingMethodsWithProducts?.shippingMethods.find(
        (shippingMethod) =>
          shippingMethod.products?.find(
            (product) => product.variant?.sku === lineItem.variant?.sku,
          ),
      ),
    );

  useEffect(() => {
    if (editingStep) {
      dispatch({
        type: 'setCurrentStep',
        value: editingStep,
      });
    } else if (
      hasSelectedShippingMethodsForAllTheLineItems &&
      !needToRecheckSelectedShippingMethods &&
      ((!isPaymentVerifying &&
        !hasCloverFormErrors &&
        paymentMethodData &&
        Object.keys(paymentMethodData).length > 0) ||
        (paypalOrderId && isShippingAddressValid(shippingAddress)) ||
        amazonPayCheckoutCreated ||
        amazonPayApproved ||
        applePaymentData ||
        isTotalCoveredByGiftcards ||
        (!!paymentToken &&
          selectedPaymentMethod === paymentMethods.CREDIT_CARD))
    ) {
      dispatch({
        type: 'setCurrentStep',
        value: REVIEW_STEP,
      });
    } else if (
      isPaymentLoading ||
      (!isCartLoading &&
        shippingAddress.phone &&
        (shippingAddress?.streetAddress || shippingAddress?.streetName) &&
        hasSelectedShippingMethodsForAllTheLineItems &&
        !needToRecheckSelectedShippingMethods)
    ) {
      dispatch({
        type: 'setCurrentStep',
        value: PAYMENT_STEP,
      });
    } else if (
      !isCartLoading &&
      shippingAddress.phone &&
      (shippingAddress?.streetAddress || shippingAddress?.streetName)
    ) {
      dispatch({
        type: 'setCurrentStep',
        value: DELIVERY_METHODS_STEP,
      });
    } else if (!isCartLoading) {
      dispatch({
        type: 'setCurrentStep',
        value: SHIPPING_INFO_STEP,
      });
    }
  }, [
    amazonPayApproved,
    amazonPayCheckoutCreated,
    applePaymentData,
    shippingAddress,
    dispatch,
    editingStep,
    hasSelectedShippingMethodsForAllTheLineItems,
    isCartLoading,
    isPaymentLoading,
    isTotalCoveredByGiftcards,
    needToRecheckSelectedShippingMethods,
    paymentMethodData,
    paypalOrderId,
    isPaymentVerifying,
    cloverFormErrors,
    hasCloverFormErrors,
    paymentToken,
    selectedPaymentMethod,
  ]);

  useEffect(() => {
    if (editingStep) {
      scrollToElement(`step${editingStep}`);
    } else if (currentStep !== REVIEW_STEP) {
      scrollToElement(`step${currentStep}`);
    } else {
      scrollToElement(COMPLETE_ORDER_BTN_ID);
    }
  }, [dispatch, editingStep, currentStep, previousCurrentStep]);

  const checkStepIsComplete = ({ step }) => {
    if (step === SHIPPING_INFO_STEP) {
      return (
        !isCartLoading &&
        !!(
          shippingAddress.phone &&
          (shippingAddress?.streetAddress || shippingAddress?.streetName)
        )
      );
    }
    if (step === DELIVERY_METHODS_STEP) {
      return (
        hasSelectedShippingMethodsForAllTheLineItems &&
        !needToRecheckSelectedShippingMethods
      );
    }
    if (step === PAYMENT_STEP) {
      return (
        !!paymentMethodData ||
        !!paypalOrderId ||
        !!amazonPayCheckoutCreated ||
        !!amazonPayApproved ||
        !!applePaymentData ||
        isTotalCoveredByGiftcards ||
        !!paymentToken
      );
    }
    return false;
  };

  const handleEditStep = ({ step }) => {
    dispatch({
      type: 'setEditingStep',
      value: step,
    });
  };

  const handleSaveShipping = useCallback(
    async (address) => {
      dispatch({ type: 'removeError' });

      if (
        selectedPaymentMethod === paymentMethods.CREDIT_CARD &&
        isSameAddressWithName(shippingAddress, billingAddress) &&
        !isSameAddressWithName(shippingAddress, address)
      ) {
        dispatch(savePaymentMethodData());
      }

      await dispatch(
        setShippingAddressSteppedCheckout({ shippingAddress: address }),
      );
      if (!billingAddress) {
        await dispatch(
          setBillingAddress({
            billingAddress: address,
          }),
        );
      }
      dispatch({
        type: 'setEditingStep',
        value: undefined,
      });
    },
    [billingAddress, dispatch, selectedPaymentMethod, shippingAddress],
  );

  const handleSaveDeliveryMethods = useCallback(
    async ({ deliveryMethods }) => {
      dispatch({ type: 'removeError' });
      dispatch(
        setShippingMethodsForAllProductsInCart({
          shippingMethods: deliveryMethods,
          overwritePaidInHomeSetupShipping: isOverwritePaidInHomeShippingABTest,
          customInHomeSetupPricing: getCookie(
            process.env.CUSTOM_IN_HOME_SETUP_PRICING_AB_TEST_COOKIE_NAME,
          )?.value,
        }),
      );

      if (selectedPaymentMethod === paymentMethods.AFFIRM) {
        dispatch(savePaymentMethodData());
      }
      dispatch(setNeedToRecheckSelectedShippingMethods(false));

      dispatch({
        type: 'setEditingStep',
        value: undefined,
      });
    },
    [dispatch, isOverwritePaidInHomeShippingABTest, selectedPaymentMethod],
  );

  const handleCompleteOrderStripe = useCallback(async () => {
    await dispatch(createStripeIntent());

    if (!billingAddress) {
      await dispatch(
        setBillingAddress({
          billingAddress: shippingAddress,
        }),
      );
    }

    const {
      paymentToken: updatedPaymentToken,
      paymentMethodData: updatedPaymentMethodData,
    } = store.getState().payments;
    // eslint-disable-next-line camelcase
    const { client_secret } = updatedPaymentMethodData ?? {};

    let parsedPaymentToken = {};
    try {
      parsedPaymentToken = JSON.parse(updatedPaymentToken);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error parsing payment token', error);
    }

    // eslint-disable-next-line no-console
    console.log('Stripe client_secret', client_secret);
    // eslint-disable-next-line no-console
    console.log('Stripe confirmation_token id', parsedPaymentToken?.id);

    const confirmPaymentResult = await stripe.confirmPayment({
      // eslint-disable-next-line camelcase
      clientSecret: client_secret,
      confirmParams: {
        confirmation_token: parsedPaymentToken?.id,
        return_url: `${window.location.origin}/checkout`,
      },
      redirect: isStripeRedirectionIfRequiredEnabled
        ? 'if_required'
        : undefined,
    });

    if (confirmPaymentResult.error) {
      // TODO: Show errors in Form
      // eslint-disable-next-line no-console
      console.error('Stripe error', confirmPaymentResult.error);
      dispatch({
        type: 'addError',
        errorBlock: PAYMENT_BLOCK,
        errorCode: CARD_TOKEN_ERROR,
      });
      dispatch({
        type: 'setStatus',
        status: 'idle',
      });
      dispatch({ type: 'setPaymentStatus', status: 'failed' });
    } else {
      // eslint-disable-next-line no-console
      console.log(
        'Stripe payment confirmed successfully, confirmPaymentResult',
        confirmPaymentResult,
      );

      dispatch(
        savePaymentMethodData({
          ...updatedPaymentMethodData,
          id: confirmPaymentResult.paymentIntent.id,
        }),
      );
      await dispatch(completeOrderSteppedCheckout());
    }
  }, [
    billingAddress,
    dispatch,
    isStripeRedirectionIfRequiredEnabled,
    shippingAddress,
    stripe,
  ]);

  const handleCompleteOrder = useCallback(async () => {
    dispatch({
      type: 'setStatus',
      status: 'loading',
    });
    if (
      selectedPaymentMethod === paymentMethods.CREDIT_CARD &&
      !!paymentToken &&
      isCloverEnabled
    ) {
      const token = JSON.parse(paymentToken)?.token;
      await dispatch(createCloverCharge({ token }));

      if (!billingAddress) {
        await dispatch(
          setBillingAddress({
            billingAddress: shippingAddress,
          }),
        );
      }
    }

    if (
      selectedPaymentMethod === paymentMethods.CREDIT_CARD &&
      isStripeEnabled
    ) {
      await turnstileService.execute(handleCompleteOrderStripe);
    } else {
      await dispatch(completeOrderSteppedCheckout());
    }
  }, [
    selectedPaymentMethod,
    paymentToken,
    isCloverEnabled,
    isStripeEnabled,
    dispatch,
    billingAddress,
    shippingAddress,
    handleCompleteOrderStripe,
  ]);

  const handleSavePaymentMethod = useCallback(
    async (data) => {
      dispatch({ type: 'removeError' });
      dispatch({ type: 'removeShippingAddressErrorCode' });

      if (selectedPaymentMethod === paymentMethods.CREDIT_CARD) {
        const placeOrderCallBackForTurnstile = async () => {
          if (isStripeEnabled) {
            if (!stripe || !elements) {
              return;
            }

            await dispatch(
              setBillingAddress({
                billingAddress: billingAddressDraft,
              }),
            );

            const {
              error: submitError,
              selectedPaymentMethod: selectedStripePaymentMethod,
            } = await elements.submit();

            if (
              selectedStripePaymentMethod ===
              process.env.STRIPE_CUSTOM_TRUEMED_ID
            ) {
              dispatch({
                type: 'setStatus',
                status: 'custom_loading',
              });
              waitForUtag(() => {
                window.utag.link({
                  event_name: 'Truemed Payment Option Clicked',
                  page_name: 'checkout',
                });
              });

              changeSelectedPaymentMethod(paymentMethods.TRUEMED);
              // @kludge: this is a workaround for the issue where the payment
              // method is reset to CREDIT_CARD coming back from the modals,
              // so the wrong logic fires. For some reason we really have to
              // click this radio button instead of just setting the method with
              // changeSelectedPaymentMethod.
              document
                .querySelector('input[data-testid="truemed-radioButton"]')
                .click();
              truemedButtonRef.current.click();
              return;
            }

            if (
              selectedStripePaymentMethod === process.env.STRIPE_CUSTOM_ACIMA_ID
            ) {
              dispatch({
                type: 'setStatus',
                status: 'custom_loading',
              });
              waitForUtag(() => {
                window.utag.link({
                  event_name: 'Acima Payment Option Clicked',
                  page_name: 'checkout',
                });
              });

              changeSelectedPaymentMethod(paymentMethods.ACIMA);
              document
                .querySelector('input[data-testid="acima-radioButton"]')
                .click();
              acimaButtonRef.current.click();
              return;
            }

            if (
              selectedStripePaymentMethod ===
              process.env.STRIPE_CUSTOM_AFFIRM_ID
            ) {
              dispatch({
                type: 'setStatus',
                status: 'custom_loading',
              });
              waitForUtag(() => {
                window.utag.link({
                  event_name: 'Affirm Payment Option Clicked',
                  page_name: 'checkout',
                });
              });

              changeSelectedPaymentMethod(paymentMethods.AFFIRM);
              document
                .querySelector('input[data-testid="affirm-radioButton"]')
                .click();
              affirmButtonRef.current.querySelector('.btn').click();
              return;
            }

            if (
              selectedStripePaymentMethod ===
              process.env.STRIPE_CUSTOM_CHARGEAFTER_ID
            ) {
              waitForUtag(() => {
                window.utag.link({
                  event_name: 'ChargeAfter Payment Option Clicked',
                  page_name: 'checkout',
                });
              });
              dispatch({
                type: 'setStatus',
                status: 'custom_loading',
              });
              changeSelectedPaymentMethod(paymentMethods.CHARGE_AFTER);
              document
                .querySelector('input[data-testid="charge-after-radioButton"]')
                .click();
              setTimeout(() => {
                chargeAfterButtonRef.current?.click();
              }, 1000);
              return;
            }

            if (
              selectedStripePaymentMethod ===
              process.env.STRIPE_CUSTOM_SHOP_PAY_ID
            ) {
              waitForUtag(() => {
                window.utag.link({
                  event_name: 'Shop Pay Payment Option Clicked',
                  page_name: 'checkout',
                });
              });
              dispatch({
                type: 'setStatus',
                status: 'custom_loading',
              });
              changeSelectedPaymentMethod(paymentMethods.SHOP_PAY);
              document
                .querySelector('input[data-testid="shopPay-radioButton"]')
                .click();
              shopPayButtonRef?.current?.shadowRoot?.firstChild?.click?.();
              return;
            }

            waitForUtag(() => {
              window.utag.link({
                event_name: `${selectedStripePaymentMethod} payment option clicked`,
                page_name: 'checkout',
              });
            });

            if (submitError) {
              // eslint-disable-next-line no-console
              console.error('Stripe: error during tokenization', submitError);
              dispatch({
                type: 'addError',
                errorBlock: PAYMENT_BLOCK,
                errorCode: CARD_TOKEN_ERROR,
              });
              dispatch({ type: 'setPaymentStatus', status: 'failed' });
              return;
            }

            const { error, confirmationToken: confirmationTokenResponse } =
              await stripe.createConfirmationToken({
                elements,
                params: {
                  payment_method_data: {
                    billing_details: {
                      email: billingAddressDraft?.email ?? '',
                      name: `${billingAddressDraft?.firstName ?? ''} ${billingAddressDraft?.lastName ?? ''}`.trim(),
                      address: {
                        line1: billingAddressDraft?.streetAddress ?? '',
                        line2: billingAddressDraft?.additionalStreetInfo ?? '',
                        city: billingAddressDraft?.city ?? '',
                        state: billingAddressDraft?.state ?? '',
                        country: billingAddressDraft?.country ?? '',
                        postal_code: billingAddressDraft?.postalCode ?? '',
                      },
                    },
                  },
                },
              });
            if (error) {
              // eslint-disable-next-line no-console
              console.error(`Stripe Error ${error.code}: ${error.message}`);
              dispatch({ type: 'setPaymentStatus', status: 'failed' });
            } else if (confirmationTokenResponse) {
              dispatch({
                type: 'setPaymentToken',
                token: JSON.stringify(confirmationTokenResponse),
              });

              if (confirmationTokenResponse.payment_method_preview?.card) {
                dispatch(
                  savePaymentMethodData({
                    id: confirmationTokenResponse.id,
                    cardData: {
                      brand:
                        confirmationTokenResponse.payment_method_preview.card
                          ?.brand,
                      expMonth:
                        confirmationTokenResponse.payment_method_preview.card?.exp_month.toString(),
                      expYear:
                        confirmationTokenResponse.payment_method_preview.card?.exp_year
                          .toString()
                          .slice(-2),
                      cardHolder:
                        confirmationTokenResponse.payment_method_preview
                          .billing_details?.name,
                      last4:
                        confirmationTokenResponse.payment_method_preview.card
                          ?.last4,
                      ...(confirmationTokenResponse.payment_method_preview
                        .card ?? {}),
                    },
                  }),
                );
              } else {
                dispatch(
                  savePaymentMethodData({
                    id: confirmationTokenResponse.id,
                    paymentData:
                      confirmationTokenResponse.payment_method_preview,
                  }),
                );
              }

              dispatch({ type: 'setPaymentStatus', status: 'succeeded' });
            }
          } else if (isCloverEnabled) {
            const tokenResult = await cloverObject.createToken();
            await dispatch(
              setBillingAddress({
                billingAddress: billingAddressDraft,
              }),
            );
            if (tokenResult?.errors) {
              setCloverFormErrors(tokenResult.errors);
              dispatch({ type: 'setPaymentStatus', status: 'failed' });
            } else {
              try {
                setCloverFormErrors({});
                dispatch({
                  type: 'setPaymentToken',
                  token: JSON.stringify(tokenResult),
                });
                dispatch({ type: 'setPaymentStatus', status: 'succeeded' });
              } catch (error) {
                dispatch({
                  type: 'addError',
                  errorBlock: PAYMENT_BLOCK,
                  errorCode: CARD_TOKEN_ERROR,
                });
                dispatch({ type: 'setPaymentStatus', status: 'failed' });
              }
            }
          }
        };
        dispatch({ type: 'setPaymentStatus', status: 'verifying' });

        if (!isStripeEnabled && isCloverEnabled) {
          await turnstileService.execute(placeOrderCallBackForTurnstile);
        } else if (isStripeEnabled) {
          await placeOrderCallBackForTurnstile();
        }
        dispatch({ type: 'setPaymentStatus', status: 'idle' });
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.PAYPAL) {
        if (
          !billingAddress ||
          !isSameFullAddress(shippingAddress, billingAddress)
        ) {
          await dispatch(
            setBillingAddress({
              billingAddress: data.address,
            }),
          );
        }
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.APPLE_PAY) {
        await Promise.all([
          dispatch(
            setBillingAddress({
              billingAddress: data.billingAddress,
            }),
          ),
          dispatch(savePaymentMethodData({})),
        ]);
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.AFFIRM) {
        dispatch(
          savePaymentMethodData({
            affirm: {
              checkout_token: data.checkout_token,
            },
          }),
        );
        handleCompleteOrder();
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.ACIMA) {
        dispatch(
          savePaymentMethodData({
            acima: {
              leaseId: data.leaseId,
              checkoutToken: data.checkoutToken,
              leaseNumber: data.leaseNumber,
            },
          }),
        );
        handleCompleteOrder();
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.TRUEMED) {
        dispatch(
          savePaymentMethodData({
            truemed: {
              id: data.paymentId,
              token: data.token,
            },
          }),
        );
        handleCompleteOrder();
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.AMAZON_PAY) {
        dispatch(savePaymentMethodData({}));
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
      if (selectedPaymentMethod === paymentMethods.CHARGE_AFTER) {
        console.log({
          title: 'ChargeAfter',
          message: `confirmationToken - ${data?.token}`,
        });
        dispatch(
          savePaymentMethodData({
            chargeAfter: {
              confirmationToken: data.token,
              lenderName:
                data.data?.lender?.name?.toUpperCase().replace(' ', '_') ?? '',
            },
          }),
        );
        handleCompleteOrder();
        dispatch({
          type: 'setEditingStep',
          value: undefined,
        });
      }
    },
    [
      billingAddress,
      billingAddressDraft,
      changeSelectedPaymentMethod,
      cloverObject,
      dispatch,
      elements,
      handleCompleteOrder,
      isCloverEnabled,
      isStripeEnabled,
      selectedPaymentMethod,
      setCloverFormErrors,
      shippingAddress,
      stripe,
    ],
  );

  const handleSave = useCallback(
    ({ step }) => {
      if (step === SHIPPING_INFO_STEP) {
        return handleSaveShipping;
      }
      if (step === DELIVERY_METHODS_STEP) {
        return handleSaveDeliveryMethods;
      }
      if (step === PAYMENT_STEP) {
        return handleSavePaymentMethod;
      }
      return () => {};
    },
    [handleSaveDeliveryMethods, handleSavePaymentMethod, handleSaveShipping],
  );

  const handleCancelDeliveryMethods = useCallback(() => {
    dispatch({
      type: 'setEditingStep',
      value: undefined,
    });
  }, [dispatch]);

  const handleCancelShipping = useCallback(() => {
    dispatch({
      type: 'setEditingStep',
      value: undefined,
    });
  }, [dispatch]);

  const handleCancelPaymentMethod = useCallback(() => {
    dispatch({
      type: 'setEditingStep',
      value: undefined,
    });
  }, [dispatch]);

  const cancelStepHandlersByStep = useMemo(
    () => ({
      [DELIVERY_METHODS_STEP]: handleCancelDeliveryMethods,
      [SHIPPING_INFO_STEP]: handleCancelShipping,
      [PAYMENT_STEP]: handleCancelPaymentMethod,
    }),
    [
      handleCancelDeliveryMethods,
      handleCancelPaymentMethod,
      handleCancelShipping,
    ],
  );

  const handleCancel = useCallback(
    ({ step }) => cancelStepHandlersByStep[step] || (() => {}),
    [cancelStepHandlersByStep],
  );

  // When unmount, should clear all payment data in localstorage.
  useEffect(() => () => storage.cleanAllPaymentData(), []);

  const lineItemsCount = cart?.lineItems.reduce(
    (items, lineItem) => items + lineItem.quantity,
    0,
  );

  useEffect(() => {
    if (isCouponCodeRedeemedValidityError) {
      setCheckoutErrorMessage(
        formatMessage(messages.couponCodeRedeemedValidityError),
      );
    } else if (isCouponCodeValidityError) {
      setCheckoutErrorMessage(formatMessage(messages.couponCodeValidityError));
    } else if (isOtherError) {
      setCheckoutErrorMessage(formatMessage(errorMessage));
    }
  }, [
    errorMessage,
    formatMessage,
    isOtherError,
    isCouponCodeRedeemedValidityError,
    isCouponCodeValidityError,
  ]);

  const showError =
    isOtherError ||
    isCouponCodeRedeemedValidityError ||
    isCouponCodeValidityError;

  const showPaypalButton = window.purTogglesEnabled('paypal_buttons') ?? false;

  const showShopPayExpressCheckout = window?.purTogglesEnabled(
    'shop_pay_express_checkout',
  );

  if (showSkeleton) {
    return <SteppedCheckoutSkeleton />;
  }

  return (
    <>
      {showSkeleton && <SteppedCheckoutSkeleton />}
      <div
        className={classnames(styles.container, {
          [styles.hidden]: showSkeleton,
        })}
      >
        <KountSdk />
        {isLoading && (
          <Spinner isModalWrapping={false} theme={THEME_TRANSPARENT} />
        )}
        <div id="error-message-general">
          <ErrorBanner
            showError={showError}
            onCloseErrorMessage={() => {
              dispatch({ type: 'removeError' });
              dispatch({ type: 'removeShippingAddressErrorCode' });
              setisCouponCodeRedeemedValidityError(false);
              setisCouponCodeValidityError(false);
            }}
          >
            {checkoutErrorMessage}
          </ErrorBanner>
        </div>

        {!!lineItemsCount && (
          <div className={styles['hidden-on-desktop']}>
            <OrderSummarySteppedWrapper
              dataSource={OrderSummaryDataSources.CART}
              title={formatMessage(messages.orderSummaryTitle)}
              isSummaryOpenDefault={false}
              isSteppedCheckout
            >
              {lineItemsCount <= 3 && (
                <OrderSummary dataSource={OrderSummaryDataSources.CART} />
              )}
              <OrderTotalSummary isSteppedCheckout />
            </OrderSummarySteppedWrapper>
          </div>
        )}
        {currentStep === 'shipping' ? (
          <div
            className={classnames(
              'layout-builder-form',
              styles['express-checkout-section'],
              {
                [styles.hidden]:
                  !showExpressCheckoutOptions || isStripeExpressCheckoutEnabled,
              },
            )}
          >
            {isLoading && <Spinner />}
            <div className={styles['express-checkout-title']}>
              {formatMessage(messages.expressTitle)}
            </div>

            <div className={styles['express-payment-buttons-container']}>
              {showShopPayExpressCheckout && (
                <div className={styles['express-button']}>
                  <ShopPayButton
                    id="shop-pay-express-checkout-button"
                    isExpressCheckout
                  />
                </div>
              )}
              {showPaypalButton && (
                <div className={styles['express-button']}>
                  <PaypalButton
                    id={PAYPALIDS.steppedExpress}
                    isPartOfMultiButtonGroup
                    isSteppedCheckout
                    isRendererButton
                    buttonsToRenderIds={[PAYPALIDS.steppedExpress]}
                    buttonsIdsThatReplacesDataWithTheInfoFromPaypal={[
                      PAYPALIDS.steppedExpress,
                    ]}
                  />
                </div>
              )}
              <div className={styles['express-button']}>
                <AmazonPayButton id="MainBlockAmzBtn" isSteppedCheckout />
              </div>
            </div>
          </div>
        ) : null}

        <div
          className={classnames(styles['express-checkout-section'], {
            [styles.hidden]: !isStripeExpressCheckoutEnabled,
            [styles['is-stripe']]: isStripeExpressCheckoutEnabled,
          })}
        >
          {showShopPayExpressCheckout && (
            <div
              className={classnames(styles['express-button'], {
                [styles['is-stripe']]: isStripeExpressCheckoutEnabled,
              })}
            >
              <ShopPayButton
                id="shop-pay-express-checkout-stripe-button"
                isExpressCheckout
              />
            </div>
          )}

          {!isTotalFromStoreZero && (
            <ExpressPayments
              onConfirmOrder={handleCompleteOrder}
              onHandleSavePaymentMethodData={handleSavePaymentMethod}
            />
          )}
        </div>

        {showAccountLoginModalLauncher && isMobile && (
          <AccountLoginModalLauncher
            linkBlockHeading={
              showShopPayEmailCapture
                ? formatMessage(messages.accountLoginLinkBlockTitleShopPay)
                : formatMessage(messages.accountLoginLinkBlockTitle)
            }
            setLoggedInCustomerId={setLoggedInCustomerId}
            mergeCart={false}
            hideCreateAccount
          />
        )}
        <div id="shop-pay-email-field-container-mobile" />
        <div id="shop-pay-signup-newsletter-container-mobile" />
        <CheckoutProgressBar
          currentStep={currentStep}
          checkStepIsComplete={checkStepIsComplete}
          editingStep={editingStep}
          onClickEdit={handleEditStep}
          isDraftCart={isDraft}
          storeTypeId={storeTypeId}
        />

        {showAccountLoginModalLauncher && !isMobile && (
          <AccountLoginModalLauncher
            linkBlockHeading={
              showShopPayEmailCapture
                ? formatMessage(messages.accountLoginLinkBlockTitleShopPay)
                : formatMessage(messages.accountLoginLinkBlockTitle)
            }
            setLoggedInCustomerId={setLoggedInCustomerId}
            mergeCart={false}
            hideCreateAccount
          />
        )}
        <div id="shop-pay-email-field-container-desktop" />
        <div id="shop-pay-signup-newsletter-container-desktop" />
        {cart?.id &&
          stepConfig.map(({ index, key, title, component: Component }) => {
            const isCompleted = checkStepIsComplete({ step: key });
            const isEditing = editingStep === key;
            const isCurrent = currentStep === key;

            return isCurrent || isCompleted || isEditing ? (
              <div id={`step${key}`} key={key}>
                <Step
                  index={index}
                  key={key}
                  stepKey={key}
                  title={formatMessage(messages[title])}
                  isCompleted={isCompleted}
                  isEditing={isEditing}
                  isCurrent={isCurrent}
                  onClickEdit={handleEditStep}
                  isDraftCart={isDraft}
                  storeTypeId={storeTypeId}
                >
                  <Component
                    showCompleted={isCompleted && !isEditing}
                    isCompleted={isCompleted}
                    showEditing={isEditing || isCurrent}
                    onClickSave={handleSave({ step: key })}
                    onClickCancel={handleCancel({ step: key })}
                    cart={cart}
                    shippingAddress={shippingAddress}
                    isEditing={isEditing}
                    truemedButtonRef={truemedButtonRef}
                    acimaButtonRef={acimaButtonRef}
                    shopPayButtonRef={shopPayButtonRef}
                    chargeAfterButtonRef={chargeAfterButtonRef}
                    affirmButtonRef={affirmButtonRef}
                  />
                </Step>
              </div>
            ) : null;
          })}
        <div
          className={classnames(
            { [styles.hidden]: !cart?.id || currentStep !== REVIEW_STEP },
            styles['complete-order-button-container'],
          )}
        >
          {isOrderLoading || isOrderSuccess ? (
            <>
              <div className={styles['processing-payment']}>
                <div className={styles['processing-payment-icon']} />
                <div className={styles['processing-payment-text']}>
                  {formatMessage(messages.processingPayment)}
                </div>
              </div>
              {isStripeRegularCheckoutEnabled && (
                <div className={styles['turnstile-widget-container']}>
                  <TurnstileWidget />
                </div>
              )}
            </>
          ) : (
            <>
              <CompleteOrderButton
                className={styles['complete-order-button']}
                onClick={handleCompleteOrder}
                section={SECTIONS.MAIN}
                isPrimary
                isSteppedCheckout
                isLoading={isOrderLoading}
                currentStep={currentStep}
              />
              {isStripeRegularCheckoutEnabled && (
                <div className={styles['turnstile-widget-container']}>
                  <TurnstileWidget />
                </div>
              )}
              <div className={styles['terms-of-use-container']}>
                <TermsOfUse />
              </div>
            </>
          )}
        </div>
        <div className={styles['hidden-on-desktop']}>
          <CheckoutGuaranties />
        </div>
        <Tealium />
      </div>
    </>
  );
};

SteppedCheckout.propTypes = {
  showExpressCheckoutOptions: PropTypes.bool,
  isDraftCheckout: PropTypes.bool,
};
SteppedCheckout.defaultProps = {
  showExpressCheckoutOptions: true,
  isDraftCheckout: false,
};

const StripeWrappedSteppedCheckout = ({
  showExpressCheckoutOptions,
  isDraftCheckout,
}) => (
  <StripeElementsContext>
    <SteppedCheckout
      showExpressCheckoutOptions={showExpressCheckoutOptions}
      isDraftCheckout={isDraftCheckout}
    />
  </StripeElementsContext>
);

StripeWrappedSteppedCheckout.propTypes = {
  showExpressCheckoutOptions: PropTypes.bool,
  isDraftCheckout: PropTypes.bool,
};
StripeWrappedSteppedCheckout.defaultProps = {
  showExpressCheckoutOptions: true,
  isDraftCheckout: false,
};

export default StripeWrappedSteppedCheckout;
